import {
  LOG_LEVEL_ERROR,
  LOG_LEVEL_INFO,
  LOG_LEVEL_WARNING,
  SCORM_FALSE,
  STATE_INITIALIZED,
  STATE_NOT_INITIALIZED, STATE_TERMINATED
} from './contants'

export default class BaseScormAPI {
  protected listenerArray: any[] = []
  protected currentState = STATE_NOT_INITIALIZED
  protected apiLogLevel = LOG_LEVEL_ERROR
  protected lastErrorCode!: number

  constructor () {
  }

  /**
   * Logging for all SCORM actions
   *
   * @param functionName
   * @param CMIElement
   * @param logMessage
   * @param messageLevel
   */
  public apiLog (functionName: string, CMIElement: any, logMessage: string, messageLevel: number) {
    logMessage = this.formatMessage(functionName, CMIElement, logMessage)

    if (messageLevel >= this.apiLogLevel) {
      switch (messageLevel) {
        case LOG_LEVEL_ERROR:
          console.error(logMessage)
          break
        case LOG_LEVEL_WARNING:
          console.warn(logMessage)
          break
        case LOG_LEVEL_INFO:
          console.info(logMessage)
          break
      }
    }
  }

  /**
   * Clears the last SCORM error code on success
   */
  public clearSCORMError (success: string) {
    if (success !== SCORM_FALSE) {
      this.lastErrorCode = 0
    }
  }

  /**
   * Formats the SCORM messages for easy reading
   *
   * @param functionName
   * @param CMIElement
   * @param message
   * @returns {string}
   */
  public formatMessage (functionName: string, CMIElement: any, message: string) {
    const baseLength = 20
    let messageString = ''

    messageString += functionName

    let fillChars = baseLength - messageString.length

    for (let i = 0; i < fillChars; i++) {
      messageString += ' '
    }

    messageString += ': '

    if (CMIElement) {
      const CMIElementBaseLength = 70

      messageString += CMIElement

      fillChars = CMIElementBaseLength - messageString.length

      for (let j = 0; j < fillChars; j++) {
        messageString += ' '
      }
    }

    if (message) {
      messageString += message
    }

    return messageString
  }

  /**
   * Returns the message that corresponds to errrorNumber
   * APIs that inherit BaseAPI should override this function
   */
  protected getLmsErrorMessageDetails (_errorNumber: number, detail = false) {
    return 'No error'
  }

  /**
   * Returns true if the API's current state is STATE_INITIALIZED
   */
  public isInitialized () {
    return this.currentState === STATE_INITIALIZED
  }

  /**
   * Returns true if the API's current state is STATE_NOT_INITIALIZED
   */
  public isNotInitialized () {
    return this.currentState === STATE_NOT_INITIALIZED
  }

  /**
   * Returns true if the API's current state is STATE_TERMINATED
   */
  public isTerminated () {
    return this.currentState === STATE_TERMINATED
  }

  /**
   * Provides a mechanism for attaching to a specific SCORM event
   *
   * @param listenerString
   * @param callback
   */
  public on (listenerString: string, callback: Function) {
    if (!callback) return

    const listenerFunctions = listenerString.split(' ')
    for (let i = 0; i < listenerFunctions.length; i++) {
      const listenerSplit = listenerFunctions[i].split('.')
      if (listenerSplit.length === 0) return

      const functionName = listenerSplit[0]

      let CMIElement = null
      if (listenerSplit.length > 1) {
        CMIElement = listenerString.replace(functionName + '.', '')
      }
      this.listenerArray.push({
        functionName: functionName,
        CMIElement: CMIElement,
        callback: callback
      })
    }
  }

  /**
   * Processes any 'on' listeners that have been created
   *
   * @param functionName
   * @param CMIElement
   * @param value
   */
  public processListeners (functionName: string, CMIElement: string | null = null, value = '') {
    for (let i = 0; i < this.listenerArray.length; i++) {
      const listener = this.listenerArray[i]
      const functionsMatch = listener.functionName === functionName
      const listenerHasCMIElement = !!listener.CMIElement
      const CMIElementsMatch = listener.CMIElement === CMIElement

      if (functionsMatch && (!listenerHasCMIElement || CMIElementsMatch)) {
        listener.callback(CMIElement, value)
      }
    }
  }

  /**
   * Throws a SCORM error
   *
   * @param errorNumber
   * @param message
   */
  public throwSCORMError (errorNumber: number, message = '') {
    if (!message) {
      message = this.getLmsErrorMessageDetails(errorNumber, false)
    }

    this.apiLog('throwSCORMError', null, errorNumber + ': ' + message, LOG_LEVEL_ERROR)

    this.lastErrorCode = errorNumber
  }

  public setLogLevel (logLevel: number) {
    this.apiLogLevel = logLevel
  }
}
